// Числа по-турецки для тренажёра «Числа на слух» (очередь идей 2026-09-17,
// п. 8, components/number-listening.tsx). Чистые функции без React:
// число → слова, генератор заданий пяти видов (число, цена, телефон, дата,
// время), проверка набранного ответа.
//
// Озвучка идёт словами, а не цифрами: так Google TTS гарантированно читает
// по-турецки, а одинаковый текст попадает в общий кэш tts_cache.

const ONES = ['', 'bir', 'iki', 'üç', 'dört', 'beş', 'altı', 'yedi', 'sekiz', 'dokuz']
const TENS = ['', 'on', 'yirmi', 'otuz', 'kırk', 'elli', 'altmış', 'yetmiş', 'seksen', 'doksan']
export const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık']
export const MONTHS_RU = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']

// Кусок числа для разбора: слова и их значение («iki yüz» = 200).
export type NumberPart = { words: string; value: number; label?: string }

// 0…999: единицы, десятки, сотни. «bir yüz» не говорят — просто «yüz».
function under1000(n: number): NumberPart[] {
  const parts: NumberPart[] = []
  const h = Math.floor(n / 100)
  const t = Math.floor((n % 100) / 10)
  const o = n % 10
  if (h) parts.push({ words: h === 1 ? 'yüz' : `${ONES[h]} yüz`, value: h * 100 })
  if (t) parts.push({ words: TENS[t], value: t * 10 })
  if (o) parts.push({ words: ONES[o], value: o })
  return parts
}

// Разбор числа 0…999 999 999 на куски: «bin» без «bir» (1000 = «bin»),
// но «bir milyon». Каждый кусок — самостоятельное слагаемое.
export function numberParts(n: number): NumberPart[] {
  if (!Number.isInteger(n) || n < 0 || n >= 1_000_000_000) throw new Error(`numberParts: ${n}`)
  if (n === 0) return [{ words: 'sıfır', value: 0 }]
  const parts: NumberPart[] = []
  const millions = Math.floor(n / 1_000_000)
  const thousands = Math.floor((n % 1_000_000) / 1000)
  const rest = n % 1000
  if (millions) parts.push({ words: `${under1000(millions).map((p) => p.words).join(' ')} milyon`, value: millions * 1_000_000 })
  if (thousands) {
    const words = thousands === 1 ? 'bin' : `${under1000(thousands).map((p) => p.words).join(' ')} bin`
    parts.push({ words, value: thousands * 1000 })
  }
  parts.push(...under1000(rest))
  return parts
}

export function numberToTr(n: number): string {
  return numberParts(n)
    .map((p) => p.words)
    .join(' ')
}

// Группа цифр телефона: ведущие нули — «sıfır», остальное — числом
// (0532 → «sıfır beş yüz otuz iki», 05 → «sıfır beş», 012 → «sıfır on iki»).
export function digitGroupToTr(group: string): string {
  const words: string[] = []
  let i = 0
  while (i < group.length - 1 && group[i] === '0') {
    words.push('sıfır')
    i++
  }
  words.push(numberToTr(Number(group.slice(i))))
  return words.join(' ')
}

export type TaskKind = 'number' | 'price' | 'phone' | 'date' | 'time'
export type NumberRange = 'small' | 'hundred' | 'thousand' | 'big'

export type ListenTask = {
  kind: TaskKind
  // Что озвучиваем.
  speech: string
  // Как показать правильный ответ цифрами.
  display: string
  // Разбор для подсказки: слова → значение.
  parts: NumberPart[]
  // Проверка набранного (см. checkAnswer).
  value: number | string
  // Дополнительно для цены: куруши, для даты: год, для времени: минуты.
  extra?: number
}

type Rng = () => number

function pick<T>(rng: Rng, items: readonly T[]): T {
  return items[Math.floor(rng() * items.length)]
}
function int(rng: Rng, min: number, max: number) {
  return min + Math.floor(rng() * (max - min + 1))
}

// Диапазоны чисел: до 20 (первые уроки), до 100, до 1000, «большие»
// (цены на жильё, зарплаты, население — тысячи и сотни тысяч).
function randomNumber(rng: Rng, range: NumberRange): number {
  switch (range) {
    case 'small':
      return int(rng, 0, 20)
    case 'hundred':
      return int(rng, 0, 100)
    case 'thousand':
      return rng() < 0.5 ? int(rng, 100, 999) : pick(rng, [100, 150, 200, 250, 300, 400, 500, 600, 750, 800, 900, 1000])
    case 'big': {
      const shapes = [
        () => int(rng, 11, 99) * 100, // 1 100 … 9 900
        () => int(rng, 1, 9) * 1000 + pick(rng, [0, 250, 500, 750]), // 1 250 … 9 750
        () => int(rng, 10, 99) * 1000, // 10 000 … 99 000
        () => int(rng, 10, 99) * 1000 + int(rng, 1, 9) * 100, // 12 500
        () => int(rng, 100, 999) * 1000, // 100 000 … 999 000
        () => int(rng, 1, 9) * 1_000_000, // миллионы
      ]
      return pick(rng, shapes)()
    }
  }
}

export function numberTask(rng: Rng, range: NumberRange): ListenTask {
  const n = randomNumber(rng, range)
  return { kind: 'number', speech: numberToTr(n), display: formatNumber(n), parts: numberParts(n), value: n }
}

// Цены: реальные суммы с рынка, кафе, такси; иногда с курушами
// («on iki lira elli kuruş»). Говорим «lira», как продавец.
const PRICE_LIRA = [5, 10, 12, 15, 18, 20, 25, 30, 35, 40, 45, 50, 60, 65, 70, 75, 80, 90, 100, 120, 150, 175, 180, 200, 250, 300, 350, 400, 450, 500, 600, 750, 850, 900, 1000, 1200, 1500, 2000, 2500, 3500, 5000]
const PRICE_KURUS = [25, 50, 75, 90, 99]

export function priceTask(rng: Rng): ListenTask {
  const lira = pick(rng, PRICE_LIRA)
  const kurus = lira < 100 && rng() < 0.35 ? pick(rng, PRICE_KURUS) : 0
  const parts: NumberPart[] = [...numberParts(lira).map((p) => ({ ...p })), { words: 'lira', value: NaN, label: '' }]
  let speech = `${numberToTr(lira)} lira`
  if (kurus) {
    speech += ` ${numberToTr(kurus)} kuruş`
    parts.push(...numberParts(kurus), { words: 'kuruş', value: NaN, label: '' })
  }
  const display = kurus ? `${formatNumber(lira)},${String(kurus).padStart(2, '0')} ₺` : `${formatNumber(lira)} ₺`
  return { kind: 'price', speech, display, parts, value: lira, extra: kurus }
}

// Телефон: мобильный 05xx xxx xx xx, читается по группам с паузами
// (запятые — для интонации TTS).
export function phoneTask(rng: Rng): ListenTask {
  const groups = [`05${int(rng, 0, 5)}${int(rng, 0, 9)}`, String(int(rng, 100, 999)), String(int(rng, 0, 99)).padStart(2, '0'), String(int(rng, 0, 99)).padStart(2, '0')]
  const parts = groups.map((g) => ({ words: digitGroupToTr(g), value: Number(g), label: g }))
  return {
    kind: 'phone',
    speech: parts.map((p) => p.words).join(', '),
    display: groups.join(' '),
    parts,
    value: groups.join(''),
  }
}

// Дата: «on beş Mayıs», иногда с годом «… iki bin yirmi altı». День — числом
// без окончания, так говорят в быту (полная форма «on beşi» — позже).
export function dateTask(rng: Rng, withYear = rng() < 0.3): ListenTask {
  const month = int(rng, 0, 11)
  const day = int(rng, 1, 30)
  const year = withYear ? int(rng, 2024, 2027) : 0
  const parts: NumberPart[] = [...numberParts(day), { words: MONTHS[month], value: month + 1, label: MONTHS_RU[month] }]
  if (year) parts.push({ words: numberToTr(year), value: year })
  const speech = `${numberToTr(day)} ${MONTHS[month]}${year ? ` ${numberToTr(year)}` : ''}`
  const display = `${String(day).padStart(2, '0')}.${String(month + 1).padStart(2, '0')}${year ? `.${year}` : ''}`
  return { kind: 'date', speech, display, parts, value: `${day}.${month + 1}`, extra: year }
}

// Время — как говорят в Турции, по 12-часовому кругу:
//   ровно        — «saat üç»                       3:00
//   половина     — «saat üç buçuk»                 3:30
//   после часа   — «saat üçü beş geçiyor»          3:05  (час в винительном + минуты + geçiyor)
//                  «saat üçü çeyrek geçiyor»       3:15  (çeyrek — четверть)
//   до часа      — «saat dörde on var»             3:50  (следующий час в дательном + минуты + var)
//                  «saat dörde çeyrek var»         3:45
// В ответе принимаем и 12-, и 24-часовую запись (см. checkAnswer).
const HOUR_ACC = ['on iki', 'biri', 'ikiyi', 'üçü', 'dördü', 'beşi', 'altıyı', 'yediyi', 'sekizi', 'dokuzu', 'onu', 'on biri', 'on ikiyi']
const HOUR_DAT = ['on iki', 'bire', 'ikiye', 'üçe', 'dörde', 'beşe', 'altıya', 'yediye', 'sekize', 'dokuza', 'ona', 'on bire', 'on ikiye']
const to12 = (h: number) => h % 12 || 12

export function timeTask(rng: Rng): ListenTask {
  const h = int(rng, 0, 23)
  const m = pick(rng, [0, 0, 30, 30, 15, 45, 5, 10, 20, 25, 35, 40, 50, 55])
  // Правильный ответ показываем так, как сказано: по 12-часовому кругу
  // (задача 23 из Telegram, 03.10.2026: «saat onu çeyrek geçiyor» и
  // «Правильно: 22:15» сбивали с толку). 24-часовую запись checkAnswer
  // принимает по-прежнему.
  const h12 = to12(h)
  const display = `${h12}:${String(m).padStart(2, '0')}`
  const parts: NumberPart[] = [{ words: 'saat', value: NaN, label: '' }]
  if (m === 0) {
    parts.push(...numberParts(h12))
  } else if (m === 30) {
    parts.push(...numberParts(h12), { words: 'buçuk', value: 30, label: 'половина' })
  } else if (m < 30) {
    parts.push({ words: HOUR_ACC[h12], value: h12, label: `${h12}-й час` })
    parts.push(m === 15 ? { words: 'çeyrek', value: 15, label: 'четверть' } : { words: numberToTr(m), value: m })
    parts.push({ words: 'geçiyor', value: NaN, label: 'минут после' })
  } else {
    const nextH = to12(h + 1)
    const left = 60 - m
    parts.push({ words: HOUR_DAT[nextH], value: nextH, label: `до ${nextH}` })
    parts.push(left === 15 ? { words: 'çeyrek', value: 15, label: 'четверть' } : { words: numberToTr(left), value: left })
    parts.push({ words: 'var', value: NaN, label: 'без' })
  }
  return { kind: 'time', speech: parts.map((p) => p.words).join(' '), display, parts, value: h, extra: m }
}

export function makeTask(rng: Rng, kind: TaskKind, range: NumberRange = 'hundred'): ListenTask {
  switch (kind) {
    case 'number':
      return numberTask(rng, range)
    case 'price':
      return priceTask(rng)
    case 'phone':
      return phoneTask(rng)
    case 'date':
      return dateTask(rng)
    case 'time':
      return timeTask(rng)
  }
}

// 12 500 → «12 500» (узкий неразрывный пробел между тысячами).
export function formatNumber(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

// Набранный ответ → верно ли. Терпимо к формату: пробелы, точки, запятые,
// «₺», «tl», «:» — всё, что человек естественно печатает на телефоне.
export function checkAnswer(task: ListenTask, raw: string): boolean {
  const s = raw.trim().toLowerCase().replace(/₺|tl|лир[аы]?|kuruş|kr\.?/g, '')
  const digits = s.replace(/\D/g, '')
  switch (task.kind) {
    case 'number':
      return digits !== '' && Number(digits) === task.value
    case 'price': {
      const m = s.replace(/\s/g, '').replace(',', '.').match(/^(\d+)(?:\.(\d{1,2}))?$/)
      if (!m) return false
      const lira = Number(m[1])
      const kurus = m[2] ? Number(m[2].padEnd(2, '0')) : 0
      return lira === task.value && kurus === (task.extra ?? 0)
    }
    case 'phone':
      return digits === task.value || `0${digits}` === task.value
    case 'date': {
      let nums: string[] | null = s.match(/\d+/g)
      // «1505», «150526», «15052026» одной группой — как набирают на цифровой клавиатуре.
      if (nums && nums.length === 1 && [4, 6, 8].includes(nums[0].length)) nums = [nums[0].slice(0, 2), nums[0].slice(2, 4), nums[0].slice(4)].filter(Boolean)
      if (!nums || nums.length < 2) return false
      const [day, month] = task.value.toString().split('.').map(Number)
      if (Number(nums[0]) !== day || Number(nums[1]) !== month) return false
      if (task.extra) {
        if (nums.length < 3) return false
        const y = Number(nums[2])
        return y === task.extra || (nums[2].length === 2 && 2000 + y === task.extra)
      }
      return true
    }
    case 'time': {
      const nums = s.match(/\d+/g)
      if (!nums) return false
      let h: number
      let m: number
      if (nums.length === 1) {
        // «1430» или «14» (ровно)
        const d = nums[0]
        if (d.length <= 2) {
          h = Number(d)
          m = 0
        } else {
          h = Number(d.slice(0, -2))
          m = Number(d.slice(-2))
        }
      } else {
        h = Number(nums[0])
        m = Number(nums[1])
      }
      if (m !== (task.extra ?? 0)) return false
      const th = task.value as number
      return h === th || (th >= 12 && h === th - 12) || (th < 12 && h === th + 12)
    }
  }
}

// Ответ набран до конца: знаков столько, сколько в правильном ответе
// (задача 21 из Telegram, 03.10.2026: проверка запускается сама, без кнопки).
// Число и телефон — по количеству цифр; цена — цифры лир и, если есть куруши,
// запятая и две цифры; дата — день, месяц (две цифры или одна от 2) и год из
// четырёх цифр; время — минуты двумя цифрами. Что набрано короче (9 вместо
// 9:00, 15.1 вместо 15.01, телефон без нуля), проверяется кнопкой, как раньше.
export function answerComplete(task: ListenTask, raw: string): boolean {
  const digits = raw.replace(/\D/g, '')
  const groups = raw.match(/\d+/g) ?? []
  switch (task.kind) {
    case 'number':
    case 'phone':
      return digits.length === String(task.value).length
    case 'price': {
      const liraLen = String(task.value).length
      if (!task.extra) return digits.length === liraLen
      return groups.length === 2 && groups[0].length === liraLen && groups[1].length === 2
    }
    case 'date': {
      const want = task.extra ? 3 : 2
      if (groups.length !== want) return false
      const last = groups[want - 1]
      return task.extra ? last.length === 4 : last.length === 2 || Number(last) > 1
    }
    case 'time':
      return groups.length === 2 && groups[1].length === 2
  }
}

// Подсказка-разбор для карточки ответа: «yüz (100) elli (50)».
export function explainParts(task: ListenTask): { words: string; value: string }[] {
  return task.parts.map((p) => ({ words: p.words, value: p.label ?? formatNumber(p.value) }))
}

// Автоформат поля ввода: разделители ставятся сами, пока человек набирает
// только цифры («0415» → «04:15», «1505» → «15.05», «0532412» → «0532 412»,
// «12500» → «12 500»). Если он сам поставил «:», «.», «,» или «/» — не трогаем.
// Разделитель добавляется только перед следующей цифрой, а не сразу после
// группы, иначе backspace не смог бы его стереть.
export function formatDigitsInput(kind: TaskKind, raw: string): string {
  if (kind === 'price') return raw
  if (/[^\d\s ]/.test(raw)) return raw
  const digits = raw.replace(/\D/g, '')
  if (!digits) return ''
  switch (kind) {
    case 'number':
      return formatNumber(Number(digits.slice(0, 9)))
    case 'phone':
      return joinGroups(digits.slice(0, 11), [4, 3, 2, 2], ' ')
    case 'time': {
      // «4» → час одной цифрой (4:15), «14» → двумя.
      const hourLen = Number(digits[0]) > 2 ? 1 : 2
      return joinGroups(digits.slice(0, hourLen + 2), [hourLen, 2], ':')
    }
    case 'date': {
      const dayLen = Number(digits[0]) > 3 ? 1 : 2
      const monthFirst = digits[dayLen]
      const monthLen = monthFirst !== undefined && Number(monthFirst) > 1 ? 1 : 2
      return joinGroups(digits.slice(0, dayLen + monthLen + 4), [dayLen, monthLen, 4], '.')
    }
  }
}

// Экранная цифра, запятая или стирание (NumPad в number-listening.tsx). Если
// разделители в поле ставил автоформат, правим цифры и форматируем заново:
// иначе после «26.11» год дописывался бы без точки («26.112026»), потому что
// formatDigitsInput не трогает строку с точкой. Свои разделители («1.5»,
// «12,5») не трогаем: просто дописываем или убираем последний знак.
export function numPadEdit(kind: TaskKind, value: string, key: string | null): string {
  const bare = value.replace(/\D/g, '')
  const auto = formatDigitsInput(kind, bare) === value
  if (key === null) return formatDigitsInput(kind, auto ? bare.slice(0, -1) : value.slice(0, -1))
  if (key === ',') return value && !value.includes(',') ? value + key : value
  return formatDigitsInput(kind, auto ? bare + key : value + key)
}

function joinGroups(digits: string, sizes: number[], sep: string): string {
  const out: string[] = []
  let i = 0
  for (const size of sizes) {
    if (i >= digits.length) break
    out.push(digits.slice(i, i + size))
    i += size
  }
  return out.join(sep)
}
